import {Injectable, OnDestroy} from '@angular/core';
import {PagamentoSpontaneoModel} from "../../../models/personal-area/urban-payments/pagamento-spontaneo.model";
import {BehaviorSubject, combineLatest, Observable, Subject, takeUntil, withLatestFrom} from "rxjs";
import {UrbanPaymentsClientService} from "./urban-payments-client.service";
import {map} from "rxjs/operators";
import {SottoTariffaModel} from "../../../models/personal-area/urban-payments/sotto-tariffa.model";

interface PagamentoSpontaneoState {
  isLoaded: boolean,
  state: PagamentoSpontaneoModel | null
}

export interface TarriffaState {
  codice: string | null,
  opzioniDiSelezione: SottoTariffaModel[]
}

interface PagamentoSpontaneoFormState {
  tariffaIniziale: TarriffaState,
  sottoTariffe: TarriffaState[],
  quantita: number,
}

@Injectable()
export class UrbanPaymentsService implements OnDestroy {

  state = new BehaviorSubject<PagamentoSpontaneoState>({
    isLoaded: false,
    state: null,
  });

  private formState = new BehaviorSubject<PagamentoSpontaneoFormState>({
    tariffaIniziale: {
      codice: null,
      opzioniDiSelezione: []
    },
    sottoTariffe: [],
    quantita: 1,
  });

  public codiceEnte = '';
  public codiceServizio = '';
  public pagamentoSpontaneo$ = this.state.pipe(map(s => s.state));
  public isAutoliquidazione$ = this.pagamentoSpontaneo$.pipe(map(s => s?.isAutoliquidazione === true))
  public pagamentoSpontaneoFormState$ = this.formState.asObservable();
  public tariffaIniziale$ = this.formState.pipe(map(s => s.tariffaIniziale));
  public sottoTariffe$ = this.formState.pipe(map(s => s.sottoTariffe));

  public importoTotale$: Observable<number | null> =
    combineLatest([this.pagamentoSpontaneoFormState$, this.pagamentoSpontaneo$, this.sottoTariffe$]).pipe(
      map(([{quantita, tariffaIniziale}, pagamentoSpontaneo, sottoTariffe]) => {
        if (sottoTariffe.length === 0 && tariffaIniziale?.codice != null) {
          const importo = pagamentoSpontaneo?.tariffario?.find(t => t.codiceTipologiaTariffa === tariffaIniziale.codice)?.importo;
          return importo != null ? importo * quantita / 100 : null ;
        }
        if (sottoTariffe.length > 0) {
          const ultimaSottoTariffa = sottoTariffe[sottoTariffe.length - 1];
          const importo = ultimaSottoTariffa.codice ?
            ultimaSottoTariffa.opzioniDiSelezione?.find(t => t.codiceTipologiaTariffa === ultimaSottoTariffa.codice)?.importo :
            null;
          return importo != null ? importo * quantita / 100 : null;
        }
        return null;
      })
    )

  public validazioniQuantita$: Observable<{
    min: number | null | undefined,
    max: number | null | undefined,
    range: number[] | null | undefined
  }> =
    combineLatest([this.pagamentoSpontaneoFormState$, this.pagamentoSpontaneo$, this.sottoTariffe$]).pipe(
      map(([{ tariffaIniziale}, pagamentoSpontaneo, sottoTariffe]) => {
        if (sottoTariffe.length === 0 && tariffaIniziale?.codice) {
          const tariffario = pagamentoSpontaneo?.tariffario?.find(t => t.codiceTipologiaTariffa === tariffaIniziale.codice);
          return {
            min: tariffario?.quantitaMinima,
            max: tariffario?.quantitaMassima,
            range: null
          }
        }
        if (sottoTariffe.length > 0) {
          const ultimaSottoTariffa = sottoTariffe[sottoTariffe.length - 1];
          const tariffario = ultimaSottoTariffa.codice ?
            ultimaSottoTariffa.opzioniDiSelezione?.find(t => t.codiceTipologiaTariffa === ultimaSottoTariffa.codice) :
            null;
          return {
            min: tariffario?.quantitaMinima,
            max: tariffario?.quantitaMassima,
            range: tariffario?.quantitaAmmesse,
          }
        }
        return {
          min: null,
          max: null,
          range: null
        };
      })
    )

  private onDestroy$ = new Subject<boolean>();
  private setSottoTariffeSelezionate$ = new Subject<string[]>();

  constructor(private client: UrbanPaymentsClientService) {
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  initState() {
    this.formState.next({
      tariffaIniziale: {
        codice: null,
        opzioniDiSelezione: []
      },
      sottoTariffe: [],
      quantita: 1,
    })
  }

  getConfigurazioneServizio(): void {
    if (this.codiceEnte == null || this.codiceServizio == null) {
      console.error('codiceEnte o codiceServizio non disponibili');
      return;
    }
    this.client.get$(this.codiceEnte, this.codiceServizio)
      .pipe(
        takeUntil(this.onDestroy$),
        map(response => response[0])
      )
      .subscribe(state => {
        this.state.next({isLoaded: true, state});
        this.setTariffaIniziale({
          codice: null,
          opzioniDiSelezione: state.tariffario
        });
      });

    this.setSottoTariffeSelezionate$
      .pipe(
        takeUntil(this.onDestroy$),
        withLatestFrom(this.pagamentoSpontaneo$)
      ).subscribe(([valoriSelezionati, pagamentoSpontaneo]) => {
      const formState = this.formState.getValue();
      if (formState.tariffaIniziale?.codice == null) {
        this.formState.next({...formState, sottoTariffe: []});
      }
      if (formState.tariffaIniziale?.codice != null && valoriSelezionati.length === 0) {
        const tariffa = pagamentoSpontaneo?.tariffario?.find(t => t.codiceTipologiaTariffa === formState.tariffaIniziale?.codice);
        const opzioniDiSelezione = tariffa?.sottoTariffe ?? [];
        if (opzioniDiSelezione.length > 0) {
          this.formState.next({
            ...formState,
            sottoTariffe:  [{
              codice: null,
              opzioniDiSelezione
            }]
          });
        } else {
          this.formState.next({
            ...formState,
            sottoTariffe: []
          });
        }

      }
      if (valoriSelezionati?.length > 0) {
        const sottoTariffe = valoriSelezionati.reduce((acc: TarriffaState[], curVal, index) => {
          if (index === 0) {
            const sottoTariffeIniziali = pagamentoSpontaneo?.tariffario
              ?.find(t => t.codiceTipologiaTariffa === formState.tariffaIniziale?.codice)?.sottoTariffe ?? [];
            acc.push({
              codice: curVal,
              opzioniDiSelezione: sottoTariffeIniziali
            });
            return acc;
          }
          const sottoTariffeCodicePadre = acc[index - 1];
          const sottoTariffeLivelloAttuale = sottoTariffeCodicePadre.opzioniDiSelezione
            .find(t => t.codiceTipologiaTariffa === sottoTariffeCodicePadre.codice)?.sottoTariffe ?? [];
          acc.push({
            codice: curVal,
            opzioniDiSelezione: sottoTariffeLivelloAttuale
          });
          return acc;
        }, [] as TarriffaState[])

        const ultimaSottoTariffa = sottoTariffe[sottoTariffe.length - 1];
        if (ultimaSottoTariffa.codice) {
          const opzioniDiSelezione = ultimaSottoTariffa.opzioniDiSelezione
            .find(t => t.codiceTipologiaTariffa === ultimaSottoTariffa.codice)?.sottoTariffe ?? []
          if (opzioniDiSelezione.length > 0) {
            sottoTariffe.push({
              codice: null,
              opzioniDiSelezione: opzioniDiSelezione
            });
          }
        }

        this.formState.next({...formState, sottoTariffe});
      }
    });
  }

  setCodiceEnteCodiceServizio(codiceEnte: string, codiceServizio: string): void {
    this.codiceEnte = codiceEnte;
    this.codiceServizio = codiceServizio;
  }


  setSottoTariffeSelezionate(codiciTariffe: string[]): void {
    this.setSottoTariffeSelezionate$.next([]);
    setTimeout(() => {
      this.setSottoTariffeSelezionate$.next(codiciTariffe);
    }, 0);
  }

  setTariffaIniziale(tariffaIniziale: TarriffaState): void {
    const formState = this.formState.getValue();
    this.formState.next({
      ...formState,
      tariffaIniziale
    });
    this.setSottoTariffeSelezionate$.next([]);
  }

  setQuantita(quantita: number) {
    const formState = this.formState.getValue();
    this.formState.next({
      ...formState,
      quantita
    });
  }
}
